import { useEffect, useState } from "react";
import { useToast } from "../hooks/useToast";
import { client } from "../apiHelper";
import { useOutletContext, useNavigate } from "react-router-dom";
import ChatInput from "../components/ui/ChatInput";
import ChatBlank from "./ChatBlank";
import ChatSystem from "./ChatSystem";
import { handleSocketMessage } from "../socketHelper";


const Chat = () => {
    const navigate = useNavigate()
    const { promptSocket, connectPromptSocket, socketStatus } = useOutletContext()
    const [queryForm, setQueryForm] = useState({ users_id: null, query: "" })
    const [hasResponse, setHasResponse] = useState({ response: null, has: false })
    const { showToast } = useToast()
    const [user, setUser] = useState(null)
    const [latestActivity, setLatestActivity] = useState([])


    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view user management.", "info");
            navigate('/login'); // Uncomment to redirect if not logged in
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Effect to fetch chat history
    useEffect(() => {
        if (user) {
            setQueryForm({ ...queryForm, users_id: user.users_id })
            client.fetchData('/inference/history', { users_id: user.users_id }).then((data) => {
                handleSocketMessage(data, setLatestActivity, hasResponse, setHasResponse)
            }).catch((error) => {
                const errMsg = error.response?.data?.detail || "Error fetching chat history"
                showToast(errMsg, "error")
                console.error(errMsg, error)
            })
        }
    }, [user, showToast])

    // Effect to handle WebSocket messages
    useEffect(() => {
        if (promptSocket && socketStatus === "Connected") {
            const handleMessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    handleSocketMessage(data, setLatestActivity, hasResponse, setHasResponse)
                } catch (error) {
                    console.error("Error parsing WebSocket message:", error)
                    showToast("Invalid message received", "error")
                }
            }
            promptSocket.addEventListener('message', handleMessage)
            return () => promptSocket.removeEventListener('message', handleMessage)
        }
    }, [promptSocket, socketStatus, showToast])

    const handleChange = () => { }

    const onChatSubmit = (e) => {
        e.preventDefault()
        if (promptSocket && promptSocket.readyState === WebSocket.OPEN && queryForm.query.trim()) {
            const payload = JSON.stringify({ query: queryForm.query, users_id: queryForm.users_id })
            promptSocket.send(payload)
            setQueryForm({ ...queryForm, query: "" })
        } else if (promptSocket && promptSocket.readyState === WebSocket.CLOSED) {
            console.log("websocket was closed. attempting reconnect...")
            connectPromptSocket()
        } else {
            console.log('Prompt websocket is not connected or available')
        }
    }

    return (

        <div className="flex h-full overflow-hidden">
            <main className="flex-1 flex flex-col bg-white px-6 overflow-hidden relative">
                {/* Fixed header: static in flex-col, always visible as body scrolls */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200 shrink-0">
                    <h2 className="text-lg font-semibold">Chat Assistant</h2>
                    <span className={`text-sm px-2 py-1 rounded-full ${socketStatus.includes("Connected") ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                        {socketStatus}
                    </span>
                </div>

                {/* Scrollable body: flex-1 ensures it takes remaining space */}
                <div className="flex-1 overflow-y-auto pr-1 pt-4 custom-scrollbar"> {/* Added pt-4 for spacing below header; custom-scrollbar for consistent styling */}
                    <div className="card border-none mb-0 flex-1 min-h-0">
                        {hasResponse.has ? <ChatSystem data={hasResponse.response} /> : <ChatBlank />}
                    </div>
                </div>

                {/* Fixed input footer: static in flex-col, always at bottom */}
                <div className="flex items-center border-t space-x-3 bg-white py-4 shrink-0">
                    <button className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v9m-5 0H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2M8 9l4-5 4 5m1 8h.01" />
                        </svg>
                    </button>
                    <button className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9v3a5.006 5.006 0 0 1-5 5h-4a5.006 5.006 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z" />
                        </svg>
                    </button>
                    <form onSubmit={onChatSubmit} className={`flex flex-1 rounded-full bg-gray-50 ${socketStatus !== "Connected" ? "opacity-50 pointer-events-none" : ""}`}>
                        <ChatInput
                            value={queryForm.query}
                            onChange={(value) => setQueryForm({ ...queryForm, query: value })}
                            disabled={socketStatus !== "Connected"}
                        />
                        <button type="submit" className="ml-2 w-8 h-8 bg-orange rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="rotate-45 w-5 h-5 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m12 18-7 3 7-18 7 18-7-3Zm0 0v-5" />
                            </svg>
                        </button>
                    </form>
                </div>
            </main>
            <aside className="w-80 bg-gray-100 text-gray-800 flex flex-col p-4 border-l border-gray-200 hidden lg:block overflow-scroll">
                <div className="flex justify-between items-center mb-4 shrink-0">
                    <h2 className="text-lg font-semibold">History</h2>
                    <span className="text-sm text-gray-500">{latestActivity?.length || 0}</span>
                </div>
                {hasResponse?.has && latestActivity?.length > 0 ? (
                    <div className="space-y-3 flex-1 overflow-y-auto pr-1 custom-scrollbar"> {/* Added custom-scrollbar for consistent styling */}
                        {[...latestActivity].reverse().map((activity, index) => (
                            <div key={index} className={
                                index === 0
                                    ? "bg-white shadow rounded-xl p-3 border border-gray-200 flex items-start space-x-2"
                                    : "bg-white rounded-xl p-3 flex items-start space-x-2"
                            }>
                                <input
                                    type="checkbox"
                                    checked={activity.d === "0"}
                                    onChange={handleChange}
                                    className="mt-1"
                                />
                                <div>
                                    <p className="font-semibold text-sm mt-2 mb-1">
                                        {activity.contextuse?.charAt(0).toUpperCase() + activity.contextuse?.slice(1) || "Unknown"}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-2 mb-1">
                                        {activity.reason || "No reason provided"}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-3 flex-1 overflow-y-auto pr-1 flex items-center justify-center custom-scrollbar"> {/* Added custom-scrollbar for consistency, though unlikely to scroll */}
                        <div className="w-full flex flex-col items-center bg-gray-200 rounded-xl animate-pulse">
                            <p className="text-xs text-gray-500 p-4">No history available</p>
                        </div>
                    </div>
                )}
                <button className="mt-4 w-full text-sm border rounded-xl py-2 text-gray-600 hover:bg-gray-100 flex items-center justify-center gap-2 bg-white shrink-0">
                    <svg className="w-4 h-4 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                    </svg>
                    Clear History
                </button>
            </aside>
        </div>


    );


}

export default Chat